<script setup>
import UploadAvatar from './components/UploadAvatar.vue';
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@stores';
import { useToast } from 'primevue/usetoast';
import { getUserFetch } from '@services/authService';

const toast = useToast();
const authStore = useAuthStore();
const { t } = useI18n({ useScope: 'global' });
const formData = ref({});
const beforeEdit = ref('');

const hasLowercase = computed(() => /[a-z]/.test(formData.value.newPassword));
const hasUppercase = computed(() => /[A-Z]/.test(formData.value.newPassword));
const hasNumeric = computed(() => /\d/.test(formData.value.newPassword));
const hasSpecialCharacter = computed(() => /[!@#$%^&*(),.?":{}]/.test(formData.value.newPassword));
const isLongEnough = computed(() => formData.value.newPassword?.length >= 8);

const resolver = () => {
    const errors = {};
    if (!formData.value.newPassword && !formData.value.password && !formData.value.confirmPassword) {
        errors.newPassword = [];
    } else if (!isLongEnough.value || !hasLowercase.value || !hasUppercase.value || !hasNumeric.value || !hasSpecialCharacter.value) {
        errors.newPassword = [{ message: t('userCreate.required') }];
    }
    if (formData.value.newPassword !== formData.value.confirmPassword) {
        errors.confirmPassword = [{ message: t('userCreate.passwordMismatch') }];
    }
    if (formData.value.newPassword && !formData.value.password) {
        errors.password = [{ message: t('userCreate.required') }];
    }
    return {
        errors,
    };
};
const getUserData = async () => {
    try {
        const data = await getUserFetch();
        formData.value = data;
        beforeEdit.value = JSON.stringify(data);
    } catch (error) {
        console.log(error);
    }
};

const changeUserData = async ({ valid }) => {
    if (!valid) return;
    if (JSON.stringify(formData.value) !== beforeEdit.value) {
        authStore.changeUserData(formData.value);
    } else {
        toast.add({ severity: 'info', summary: t('userCreate.noChanges'), life: 3000 });
    }
};
onMounted(() => {
    getUserData();
});
</script>

<template>
    <Form v-slot="$form" class="profile-card" @submit="changeUserData" :resolver="resolver" :initialValues="formData">
        <h1 class="profile-title">{{ t('userCreate.profile') }}</h1>
        <UploadAvatar v-model="formData.image" :name="formData.full_name" />
        <div class="profile-content">
            <div class="input-wrapper">
                <label for="fullname" class="input-label">{{ t('userCreate.fullName') }}</label>
                <InputText v-model="formData.full_name" inputId="fullname" fluid :placeholder="t('userCreate.fullName')" />
            </div>

            <div class="input-wrapper">
                <label for="username" class="input-label">{{ t('userCreate.username') }}</label>
                <InputText v-model="formData.username" inputId="username" fluid :placeholder="t('userCreate.username')" disabled />
            </div>
        </div>
        <h1 class="profile-subtitle">{{ t('userCreate.setPassword') }}</h1>
        <div class="profile-content">
            <div class="input-wrapper">
                <label for="newPassword" class="input-label">{{ t('userCreate.newPassword') }}</label>
                <Password v-model="formData.newPassword" name="newPassword" feedback toggleMask :prompt="false" fluid>
                    <template #footer>
                        <Divider />
                        <ul class="pl-2 ml-2 my-0 leading-normal">
                            <li :class="{ 'text-green-600': hasLowercase, 'text-red-600': !hasLowercase }">{{ t('userCreate.lowercase') }}</li>
                            <li :class="{ 'text-green-600': hasUppercase, 'text-red-600': !hasUppercase }">{{ t('userCreate.uppercase') }}</li>
                            <li :class="{ 'text-green-600': hasNumeric, 'text-red-600': !hasNumeric }">{{ t('userCreate.number') }}</li>
                            <li :class="{ 'text-green-600': isLongEnough, 'text-red-600': !isLongEnough }">{{ t('userCreate.length') }}</li>
                            <li :class="{ 'text-green-600': hasSpecialCharacter, 'text-red-600': !hasSpecialCharacter }">{{ t('userCreate.symbol') }} (!@#$%^&*(),.?":{})</li>
                        </ul>
                    </template>
                </Password>
                <Message v-if="$form.newPassword?.invalid" severity="error" size="small" variant="simple">{{ $form.newPassword.error?.message }}</Message>
            </div>

            <div class="input-wrapper">
                <label for="confirmPassword" class="input-label">{{ t('userCreate.confirmPassword') }}</label>
                <Password v-model="formData.confirmPassword" name="confirmPassword" :feedback="false" toggleMask fluid />
                <Message v-if="$form.confirmPassword?.invalid" severity="error" size="small" variant="simple">{{ $form.confirmPassword.error?.message }}</Message>
            </div>

            <div class="input-wrapper">
                <label for="password" class="input-label">{{ t('userCreate.currentPassword') }}</label>
                <Password v-model="formData.password" name="password" :feedback="false" toggleMask fluid />
                <Message v-if="$form.password?.invalid" severity="error" size="small" variant="simple">{{ $form.password.error?.message }}</Message>
            </div>
        </div>
        <div class="save-buttons">
            <Button size="large" type="submit" :label="t('global.save')" severity="info"></Button>
        </div>
    </Form>
</template>

<style scoped>
.profile-card {
    @apply bg-white py-8 rounded-[6px];
}

.profile-title {
    @apply text-xl font-bold px-8 pb-4;
}

.profile-subtitle {
    @apply text-lg font-bold px-8;
}

.profile-content {
    @apply flex gap-4 py-6 px-8 items-start;
}

.input-wrapper {
    @apply w-full flex flex-col gap-2;
}

.input-label {
    @apply font-semibold;
}

.save-buttons {
    @apply flex justify-end gap-2 items-center px-8;
}
</style>
